<script setup>
import { ref, onMounted } from 'vue';

const props = defineProps({
    exitUrl: { type: String, required: true },
    generateUrl: { type: String, required: true },
    pollUrlPrefix: { type: String, required: true },
    conversationUrl: { type: String, required: true },
    newConversationUrl: { type: String, required: true },
    conversationsUrl: { type: String, default: '' },
    conversationId: { type: [String, Number], default: null },
    initialMessage: { type: String, default: '' },
});

const messages = ref([]);
const context = ref({});
const currentConversationId = ref(props.conversationId);
const isGenerating = ref(false);
const pollTimer = ref(null);
const showHistory = ref(false);
const conversationHistory = ref([]);

const loadConversation = async () => {
    try {
        const response = await fetch(props.conversationUrl);
        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = data.messages || [];
        context.value = data.context || {};
    } catch (e) {
        console.error('Failed to load conversation', e);
    }
};

const loadHistory = async () => {
    if (!props.conversationsUrl) return;
    try {
        const response = await fetch(props.conversationsUrl);
        conversationHistory.value = await response.json();
    } catch (e) {
        console.error('Failed to load history', e);
    }
};

const toggleHistory = async () => {
    showHistory.value = !showHistory.value;
    if (showHistory.value && !conversationHistory.value.length) {
        await loadHistory();
    }
};

const loadConversationById = async (id) => {
    if (!props.conversationsUrl) return;
    try {
        const response = await fetch(`${props.conversationsUrl}/${id}`);
        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = data.messages || [];
        context.value = data.context || {};
        showHistory.value = false;
    } catch (e) {
        console.error('Failed to load conversation', e);
    }
};

const formatDate = (iso) => {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const sendMessage = async (text) => {
    if (isGenerating.value) return;
    messages.value.push({ role: 'user', content: text });
    isGenerating.value = true;

    try {
        const response = await fetch(props.generateUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
            body: JSON.stringify({ prompt: text }),
        });

        const data = await response.json();
        currentConversationId.value = data.conversation_id;

        if (data.status === 'budget_exceeded') {
            isGenerating.value = false;
            pushFailureMessage(data.status);
            return;
        }

        startPolling();
    } catch (e) {
        console.error('Failed to send message', e);
        isGenerating.value = false;
    }
};

const pushFailureMessage = (status) => {
    messages.value.push({
        role: 'assistant',
        content: status === 'budget_exceeded'
            ? _mft('data-analyst:susan.budgetExceeded')
            : _mft('data-analyst:susan.errorMessage'),
        budgetExceeded: status === 'budget_exceeded',
    });
};

const startPolling = () => {
    if (pollTimer.value) clearInterval(pollTimer.value);

    pollTimer.value = setInterval(async () => {
        try {
            const response = await fetch(`${props.pollUrlPrefix}${currentConversationId.value}/poll`);
            const data = await response.json();

            if (data.status === 'completed') {
                stopPolling();
                isGenerating.value = false;

                messages.value.push({
                    role: 'assistant',
                    content: data.message,
                    charts: data.charts,
                });
            } else if (data.status === 'failed' || data.status === 'budget_exceeded') {
                stopPolling();
                isGenerating.value = false;
                pushFailureMessage(data.status);
            }
        } catch (e) {
            console.error('Polling failed', e);
        }
    }, 2000);
};

const stopPolling = () => {
    if (pollTimer.value) {
        clearInterval(pollTimer.value);
        pollTimer.value = null;
    }
};

const startNewConversation = async () => {
    try {
        const response = await fetch(props.newConversationUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]')?.content,
            },
        });

        const data = await response.json();
        currentConversationId.value = data.id;
        messages.value = [];
        context.value = {};
    } catch (e) {
        console.error('Failed to create new conversation', e);
    }
};

onMounted(() => {
    loadConversation();
});
</script>

<template>
    <div class="flex flex-col h-screen bg-gray-50">
        <!-- Header -->
        <div class="flex items-center justify-between px-4 py-3 bg-white border-b border-gray-200">
            <div class="flex items-center gap-3">
                <a :href="exitUrl" class="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                        <path fill-rule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clip-rule="evenodd" />
                    </svg>
                </a>
                <div class="flex items-center gap-2">
                    <div class="w-8 h-8 rounded-full bg-aiAgentDataAnalyst flex items-center justify-center">
                        <ai-agent-data-analyst-avatar class="w-full h-full" />
                    </div>
                    <div>
                        <h1 class="text-sm font-semibold text-gray-900">
                            <mf-translate domainkey="data-analyst:susan.title"></mf-translate>
                        </h1>
                        <p class="text-xs text-gray-500 mb-0">
                            <mf-translate domainkey="data-analyst:susan.subtitle"></mf-translate>
                        </p>
                    </div>
                </div>
            </div>
            <div class="flex items-center gap-2">
                <button-default
                    v-if="conversationsUrl"
                    @click="toggleHistory"
                    size="sm"
                >
                    <template #icon>
                        <svg class="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                        </svg>
                    </template>
                    <mf-translate domainkey="data-analyst:susan.history"></mf-translate>
                </button-default>
                <button-secondary
                    @click="startNewConversation"
                    size="sm"
                >
                    <mf-translate domainkey="data-analyst:susan.newAnalysis"></mf-translate>
                </button-secondary>
            </div>
        </div>

        <!-- History panel -->
        <transition name="slide">
            <div v-if="showHistory" class="border-b border-gray-200 bg-white max-h-64 overflow-y-auto">
                <div v-if="!conversationHistory.length" class="p-4 text-center text-sm text-gray-400">
                    <mf-translate domainkey="data-analyst:susan.noHistory"></mf-translate>
                </div>
                <div v-else class="divide-y divide-gray-50">
                    <button
                        v-for="item in conversationHistory"
                        :key="item.id"
                        @click="loadConversationById(item.id)"
                        class="w-full text-left px-4 py-3 hover:bg-gray-50 transition-colors"
                        :class="{ 'bg-aiAgentDataAnalyst/40': item.id === currentConversationId }"
                    >
                        <div class="flex items-center gap-2">
                            <span v-if="item.scheduled" class="flex-shrink-0 px-1.5 py-0.5 text-xs font-medium bg-aiAgentDataAnalyst text-gray-800 rounded">
                                <mf-translate domainkey="data-analyst:susan.scheduled"></mf-translate>
                            </span>
                            <span class="text-sm text-gray-700 truncate">{{ item.schedule_name || item.preview || _mft('data-analyst:susan.newAnalysis') }}</span>
                            <span class="flex-shrink-0 ml-auto text-xs text-gray-400">{{ formatDate(item.created_at) }}</span>
                        </div>
                        <p v-if="item.scheduled && item.preview" class="mt-0.5 text-xs text-gray-400 truncate">{{ item.preview }}</p>
                    </button>
                </div>
            </div>
        </transition>

        <!-- Chat -->
        <div class="flex-1 flex flex-col overflow-hidden">
            <susan-chat
                :messages="messages"
                :is-generating="isGenerating"
                :initial-message="initialMessage"
                @send="sendMessage"
            ></susan-chat>
        </div>
    </div>
</template>

<style scoped>
.slide-enter-active,
.slide-leave-active {
    transition: all 0.2s ease;
}
.slide-enter-from,
.slide-leave-to {
    opacity: 0;
    max-height: 0;
}
</style>
